import { useMemo } from 'react'
import type { StepUpActivationCallbacks } from '@devnw/cyber-iam/auth/StepUpActivation/useStepUpActivation'
import { HTTPError } from '../../../gen/client/react'
import { useAdminGenClientNoAutoLogout } from '../api/genClient'
import { useAuth } from './AuthContext'
import { runAuthenticationCeremony, runRegistrationCeremony } from './webauthnCeremony'

/**
 * Builds the `StepUpActivationCallbacks` transport @devnw/cyber-iam's
 * GrantActivationPanel needs (OIDCPAM Gate-6 Phase 5a). GrantActivationPanel
 * owns the activating/checking-enrollment/registering-passkey/
 * authenticating/retrying/done/error state machine itself (via its internal
 * `useStepUpActivation`) — this hook supplies ONLY the four injected
 * transport calls. Every wire call below is byte-for-byte the same DPoP +
 * activate + register-if-needed + authenticate + retry chain the
 * pre-migration `useActivateWithStepUp` hook ran inline; only the shape
 * changed (one combined `activate()` -> four separably-orchestrated
 * callbacks) to match the library's injected-transport contract. The DPoP
 * proof itself is generated inside the INJECTED `activateRequest` (the admin
 * or self-service `accessRequestsApi.activate` — see
 * `api/accessRequestsClient.ts`) and is untouched by this migration.
 *
 * The activate call is INJECTED (not built here) so one step-up transport
 * serves BOTH the tenant-scoped ADMIN detail page (AccessRequestDetail) and
 * the ownership-checked SELF-SERVICE cross-app hand-off (ActivateGrant).
 * This mirrors origin's production hardening, which decoupled the retired
 * `useActivateWithStepUp` hook the same way: ActivateGrant must activate via
 * the `/v1/pam/me` self-service surface (no tenant, no admin role), never the
 * admin API. The 401 step-up sentinel + retry behavior is identical for both.
 */

const STEP_UP_REQUIRED_MESSAGE =
  'privileged activation requires a valid privileged session'

/**
 * Builds the failure message for a rejected authenticate/complete response.
 * The server always writes `{"error": "<message>"}` on failure
 * (writeJSONError, gen/api/handlers_gen.go) with the ACTUAL reason the
 * ceremony was rejected. This raw fetch bypasses the generated client (see
 * `authenticate`'s doc note below on why), so unlike every other call in
 * this flow it must parse that body itself instead of getting HTTPError's
 * message for free. Falls back to the bare status code if the body isn't
 * JSON (or is empty).
 */
async function stepUpFailureMessage(resp: Response): Promise<string> {
  try {
    const body = (await resp.json()) as { error?: string }
    if (body.error) {
      return `Step-up authentication failed: ${body.error}`
    }
  } catch {
    // Body wasn't JSON (or was empty) — fall through to the generic message.
  }
  return `Step-up authentication failed: ${resp.status}`
}

export function useGrantActivationCallbacks(
  activateRequest: () => Promise<void>
): StepUpActivationCallbacks {
  const { claims } = useAuth()
  // The webauthn step-up ceremony (enrollment/register/authenticate) routes
  // through the no-auto-logout client: a 401 during activation means
  // "step-up required" (recoverable), not "your session is dead" — see
  // useAdminGenClientNoAutoLogout's doc comment in genClient.ts. The injected
  // activateRequest carries its own (admin or self-service) no-auto-logout
  // transport + DPoP proof; see api/accessRequestsClient.ts.
  const client = useAdminGenClientNoAutoLogout()

  return useMemo<StepUpActivationCallbacks>(
    () => ({
      async activate() {
        try {
          await activateRequest()
        } catch (e: unknown) {
          const isStepUpRequired =
            e instanceof HTTPError && e.status === 401 && e.message === STEP_UP_REQUIRED_MESSAGE
          if (isStepUpRequired) return 'step-up-required'
          throw e instanceof Error ? e : new Error('Activation failed')
        }
      },
      async checkEnrollment() {
        // Mirrors the pre-migration guard's placement: bail out before ever
        // touching the enrollment/registration/authentication endpoints when
        // there's no signed-in subject to register/authenticate as (this ran
        // immediately after step-up-required was detected, before entering
        // the "checking-enrollment" phase).
        if (!claims?.sub) {
          throw new Error(
            'Unable to complete step-up authentication: no signed-in user was found. Please sign in again and retry.'
          )
        }
        const status = await client.webAuthnEnrollmentStatus({})
        return status.enrolled
      },
      async registerPasskey() {
        const begin = await client.postRegisterBegin({ userId: claims?.sub ?? '' })
        const { payload } = await runRegistrationCeremony(begin.options as never)
        await client.postRegisterComplete({
          sessionId: begin.sessionId,
          credential: payload,
        })
      },
      // step_up/return_to are carried as URL query parameters, not JSON body
      // fields (server-side: to dodge the schema's RejectUnknownMembers
      // guard), so the generated client — which only has a schema-typed
      // body, no arbitrary extra query params — can't express this one
      // call. A plain fetch is used for that single request; every other
      // call goes through the generated client.
      async authenticate() {
        const authBegin = await client.postAuthenticateBegin({
          clientId: 'admin-ui-stepup',
          redirectUri: 'urn:oidc-admin-ui:stepup',
          codeChallenge: 'stepup-inline-not-oauth',
          codeChallengeMethod: 'S256',
        })
        const assertion = await runAuthenticationCeremony(authBegin.options as never)
        const completeResp = await fetch(
          '/v1/oidc/webauthn/authenticate/complete?step_up=1&return_to=' +
            encodeURIComponent('/authorize'),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              session_id: authBegin.sessionId,
              credential: assertion,
            }),
          }
        )
        if (!completeResp.ok) {
          throw new Error(await stepUpFailureMessage(completeResp))
        }
      },
    }),
    [activateRequest, client, claims]
  )
}
